import { Map, List } from 'immutable'
import _get from 'lodash/get'
import _isUndefined from 'lodash/isUndefined'
import _toNumber from 'lodash/toNumber'
import { getAuthIsLoggedIn } from '../auth/index'
import { filterEntitlementsByContentSetAndSku } from '../user/entitlements'
import {
  USER_ACCOUNT_TRANSACTION_STATUS_APPROVED,
  USER_ACCOUNT_TRANSACTION_STATUS_DECLINED,
} from '../user-account'
import {
  isCheckoutFlowPage,
  isSinglePageCheckout,
  isSharePage,
  isLivePage,
  isFullPlayer,
  isAccountChangePlan,
  isAccountPauseFlow,
  isAccountCancelFlow,
  isLogout,
  isRemoteAccountCreate,
  isLoginWithLink,
  isActivate,
  isGo,
  isPolicy,
  isGetStarted,
} from '../url/index'

export const PAYMENT_FAILED_ENTITLEMENT_MODAL_DEV_THRESHOLD_MS = 2 * 60 * 1000
export const PAYMENT_FAILED_ENTITLEMENT_MODAL_PROD_THRESHOLD_MS = 24 * 60 * 60 * 1000

export function getPaymentFailedEntitlementModalThresholdMs (isProdTiming) {
  return isProdTiming
    ? PAYMENT_FAILED_ENTITLEMENT_MODAL_PROD_THRESHOLD_MS
    : PAYMENT_FAILED_ENTITLEMENT_MODAL_DEV_THRESHOLD_MS
}

export function getPaymentFailedEntitlementModalOptionsFromConfig (config) {
  const isProdTiming = !!_get(
    config,
    ['features', 'checkout', 'paymentFailedEntitlementModalProdTiming'],
    0,
  )

  return {
    featureEnabled: !!_get(config, ['features', 'checkout', 'paymentFailedEntitlementMessage']),
    thresholdMs: getPaymentFailedEntitlementModalThresholdMs(isProdTiming),
    isProdTiming,
  }
}

export function getPaymentFailedEntitlementTracking (featureTracking) {
  const trackingData = featureTracking.getIn(['data', 'checkoutPaymentFailedEntitlementMessage'])
  if (!trackingData) {
    return null
  }

  const checkoutDate = Map.isMap(trackingData)
    ? trackingData.get('checkoutDate')
    : _get(trackingData, 'checkoutDate')
  const seenCount = Map.isMap(trackingData)
    ? trackingData.get('seenCount')
    : _get(trackingData, 'seenCount')

  if (_isUndefined(seenCount) || _toNumber(seenCount) !== 0) {
    return null
  }

  return { checkoutDate, seenCount: _toNumber(seenCount) }
}

export function getSoonestEligibleEntitlementWindow (state, checkoutDateFallback) {
  const { user = Map() } = state
  const entitlementsList = user.getIn(['data', 'userEntitlements'], List()) || List()
  const filtered = filterEntitlementsByContentSetAndSku(entitlementsList)
  let soonestWindow = null

  filtered.forEach((entitlement) => {
    const entitlementEndDateString = entitlement.get('end')
    if (!entitlementEndDateString) {
      return
    }

    const endTime = parseInt(entitlementEndDateString, 10) * 1000
    if (Number.isNaN(endTime)) {
      return
    }

    const entitlementStartDateString = entitlement.get('start')
    let startTime = entitlementStartDateString
      ? parseInt(entitlementStartDateString, 10) * 1000
      : null
    if (startTime !== null && Number.isNaN(startTime)) {
      startTime = null
    }

    if (
      soonestWindow === null
      || endTime < soonestWindow.endTime
    ) {
      soonestWindow = { startTime, endTime }
    }
  })

  if (!soonestWindow) {
    return null
  }

  if (soonestWindow.startTime === null && checkoutDateFallback) {
    const checkoutStartTime = new Date(checkoutDateFallback).getTime()
    if (!Number.isNaN(checkoutStartTime)) {
      soonestWindow.startTime = checkoutStartTime
    }
  }

  return soonestWindow
}

export function isWithinPaymentFailedEntitlementModalThreshold ({
  startTime,
  endTime,
  checkoutDate,
}, options = {}) {
  const {
    isProdTiming = false,
    now = Date.now(),
  } = options
  const thresholdMs = options.thresholdMs
    ?? getPaymentFailedEntitlementModalThresholdMs(isProdTiming)

  if (!endTime || now >= endTime) {
    return false
  }

  if (!isProdTiming) {
    const checkoutStartTime = checkoutDate ? new Date(checkoutDate).getTime() : null
    const entitlementStartTime = checkoutStartTime || startTime

    if (!entitlementStartTime || Number.isNaN(entitlementStartTime)) {
      return false
    }

    return now - entitlementStartTime >= thresholdMs
  }

  return endTime - now <= thresholdMs
}

export function shouldShowPaymentFailedEntitlementModal (state, options = {}) {
  const {
    featureEnabled = true,
    isProdTiming = false,
    now = Date.now(),
  } = options
  const thresholdMs = options.thresholdMs
    ?? getPaymentFailedEntitlementModalThresholdMs(isProdTiming)

  if (!featureEnabled) {
    return false
  }

  const {
    app,
    auth,
    featureTracking,
    dialog,
    userAccount,
  } = state

  if (!app.get('bootstrapComplete') || !getAuthIsLoggedIn(auth)) {
    return false
  }

  const activeDialog = dialog.get('componentName', null)
  if (featureTracking.get('processing') || activeDialog) {
    return false
  }

  const tracking = getPaymentFailedEntitlementTracking(featureTracking)
  if (!tracking || tracking.seenCount !== 0) {
    return false
  }

  if (!Map.isMap(userAccount)) {
    return false
  }

  const lastPaymentStatus = userAccount.getIn(
    ['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'],
    null,
  )
  const paymentDeclined = lastPaymentStatus === USER_ACCOUNT_TRANSACTION_STATUS_DECLINED
  if (!paymentDeclined) {
    return false
  }

  const entitlementWindow = getSoonestEligibleEntitlementWindow(state, tracking.checkoutDate)
  if (!entitlementWindow) {
    return false
  }

  return isWithinPaymentFailedEntitlementModalThreshold({
    ...entitlementWindow,
    checkoutDate: tracking.checkoutDate,
  }, {
    thresholdMs,
    isProdTiming,
    now,
  })
}

export function shouldMarkPaymentFailedEntitlementModalSeen (state) {
  const { featureTracking, userAccount } = state

  if (featureTracking.get('processing')) {
    return false
  }

  const tracking = getPaymentFailedEntitlementTracking(featureTracking)
  if (!tracking) {
    return false
  }

  if (!Map.isMap(userAccount)) {
    return false
  }

  // if the last transaction status is approved, mark the modal as seen
  // if the user has updated their payment method, or a payment re-try has happened,
  // the status can change to approved, so we need to check for that
  return userAccount.getIn(
    ['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'],
    null,
  ) === USER_ACCOUNT_TRANSACTION_STATUS_APPROVED
}

export function isPaymentFailedEntitlementModalPage ({ state }) {
  const { resolver, dialog } = state
  const path = resolver.getIn(['data', 'path'])
  const query = resolver.getIn(['data', 'query'])
  const activeDialog = dialog.get('componentName', null)

  if (activeDialog) {
    return false
  }

  return (
    !isCheckoutFlowPage(path)
    && !isSinglePageCheckout(path)
    && !isSharePage(path)
    && !isLivePage(path)
    && !isFullPlayer(query)
    && !isAccountChangePlan(path)
    && !isAccountPauseFlow(path)
    && !isAccountCancelFlow(path)
    && !isLogout(path)
    && !isRemoteAccountCreate(path)
    && !isLoginWithLink(path)
    && !isActivate(path)
    && !isGo(path)
    && !isPolicy(path)
    && !isGetStarted(path)
  )
}
